import { useState, useEffect } from 'react';
import { HiPlus, HiPencil, HiTrash } from 'react-icons/hi';
import { sizesService } from '../api/services';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import { TableSkeleton } from '../components/Skeleton';
import { sanitizeText, sanitizeErrorMessage } from '../utils/sanitize';
import { sizeSchema, validateForm } from '../utils/validation';
import toast from 'react-hot-toast';

export default function Sizes() {
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSize, setEditingSize] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    is_active: true,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchSizes();
  }, []);

  const fetchSizes = async () => {
    try {
      const response = await sizesService.getAll();
      setSizes(response.data);
    } catch (error) {
      toast.error(sanitizeErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validate form
    const validation = validateForm(sizeSchema, formData);
    if (!validation.success) {
      setErrors(validation.errors);
      return;
    }

    setSubmitting(true);
    try {
      if (editingSize) {
        await sizesService.update(editingSize.id, formData);
        toast.success('Size updated successfully');
      } else {
        await sizesService.create(formData);
        toast.success('Size created successfully');
      }

      setShowModal(false);
      resetForm();
      fetchSizes();
    } catch (error) {
      toast.error(sanitizeErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (size) => {
    setEditingSize(size);
    setFormData({
      name: size.name || '',
      is_active: size.is_active ?? true,
    });
    setErrors({});
    setShowModal(true);
  };

  const handleDelete = async (size) => {
    if (window.confirm(`Are you sure you want to delete "${sanitizeText(size.name)}"?`)) {
      try {
        await sizesService.delete(size.id);
        toast.success('Size deleted successfully');
        fetchSizes();
      } catch (error) {
        toast.error(sanitizeErrorMessage(error));
      }
    }
  };

  const resetForm = () => {
    setEditingSize(null);
    setFormData({
      name: '',
      is_active: true,
    });
    setErrors({});
  };

  const columns = [
    { key: 'name', label: 'Size Name', render: (row) => sanitizeText(row.name) },
    {
      key: 'is_active',
      label: 'Status',
      render: (row) => (
        <span className={`status-badge ${row.is_active ? 'bg-green-900/50 text-green-300 border border-green-700' : 'bg-gray-800 text-gray-400 border border-gray-600'}`}>
          {row.is_active ? 'ACTIVE' : 'INACTIVE'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
            className="p-2 text-gray-400 hover:text-gold-500 transition-colors"
            aria-label="Edit size"
          >
            <HiPencil className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
            aria-label="Delete size"
          >
            <HiTrash className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold gold-gradient-text">Sizes</h1>
          <p className="text-gray-400 mt-1">Manage catalogue sizes (e.g., 60x60, 80x80)</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
        >
          <HiPlus className="w-5 h-5" />
          Add Size
        </button>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <TableSkeleton rows={5} columns={3} />
        ) : (
          <DataTable columns={columns} data={sizes} />
        )}
      </div>

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editingSize ? 'Edit Size' : 'Add New Size'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Size Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (errors.name) setErrors({ ...errors, name: null });
              }}
              className={`input-field ${errors.name ? 'border-red-500 focus:border-red-500' : ''}`}
              placeholder="e.g., 60x60, 80x80, 120x60"
              maxLength={50}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-400">{errors.name}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded border-dark-300 bg-dark-500 text-gold-500 focus:ring-gold-500"
              />
              <span className="text-sm text-gray-300">Active</span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="btn-primary flex-1 flex items-center justify-center"
              disabled={submitting}
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-dark-900 border-t-transparent rounded-full animate-spin" />
              ) : (
                editingSize ? 'Update Size' : 'Create Size'
              )}
            </button>
            <button
              type="button"
              onClick={() => { setShowModal(false); resetForm(); }}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
